import { Swing } from "./extras";

// "The beauty of faith": a cliff's edge at night, a full moon → a cherry blossom tree blooms with a swing → swing her toward the moon → the painting.
export const faith = {
  slug: "the-beauty-of-faith",
  duration: 28,
  extra: Swing,
  captionTail: "My mother calls this my self-portrait.",
  stages: [
    { id: "edge", at: 0, hint: "the edge of a cliff, at night" },
    { id: "tree", at: 6, hint: "a tree, at the very edge" },
    { id: "swing", at: 11, hint: "drag or tap to swing" },
    { id: "settle", at: 22, hint: "" },
  ],
  touch: { kind: "swing", from: 11, until: 22 },
  line: { text: "Faith is finding a tree to swing from, even at the edge of a cliff.", from: 14, to: 27.5, pos: "low" },
  cues: [[9, "owl"], [17.5, "owl"]],
  ambient: { scene: "night", until: 99, then: [[17, "nightCalm"]] },
  look: { start: -0.05, lift: [2, 7], end: 0.16, yaw: [[0, 0.4], [4, -0.3], [8, 0], [28, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "faith", pos: "low" },
  speed: [[0, 1], [28, 1]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface;
varying vec3 vPos;
${"__NOISE__"}
float hash(float n){ return fract(sin(n * 127.1) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
float seg(vec2 p, vec2 a, vec2 b){ vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float n2 = snoise(vPos * 3.0 + 4.0) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float grow = smoothstep(5.5, 9.5, uT);
  float bloomK = smoothstep(8.0, 11.5, uT);
  float settle = smoothstep(22.0, 27.0, uT);
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    float y = vPos.y;
    col = mix(vec3(0.15, 0.17, 0.25), vec3(0.05, 0.06, 0.12), smoothstep(1.0, 6.0, y)) + 0.02 * n;
    // the huge full moon rising
    vec2 mc = vec2(X + 0.15, y - mix(0.6, 3.3, smoothstep(0.0, 8.0, uT)));
    float md = length(mc);
    col += vec3(1.0, 0.95, 0.85) * exp(-md * 0.9) * 0.22;
    col = mix(col, vec3(0.98, 0.95, 0.88) - 0.07 * snoise(vec3(mc * 3.0, 1.0)), 1.0 - smoothstep(1.05, 1.1, md));
    // the drop: the valley lost in dark mist below the edge
    float hz = 1.15 + 0.12 * sin(X * 0.7) + 0.06 * sin(X * 2.3);
    float mist = snoise(vec3(X * 0.4 + uT * 0.05, y * 1.5, 3.0)) * 0.5 + 0.5;
    col = mix(col, mix(vec3(0.05, 0.06, 0.1), vec3(0.18, 0.2, 0.28), mist * 0.6), step(y, hz) * (1.0 - smoothstep(-0.3, 0.3, abs(th) - 1.2)));
    float ridge = step(y, hz + 0.25 * smoothstep(1.0, 1.6, abs(th))) * smoothstep(0.9, 1.4, abs(th));
    col = mix(col, vec3(0.04, 0.04, 0.06), ridge);
    // the cherry blossom tree growing at the very edge, the swing's branch reaching over
    vec2 p = vec2(X, y);
    float tr = seg(p, vec2(-1.6, 0.0), vec2(-1.45, mix(0.2, 2.6, grow)));
    tr = min(tr, seg(p, vec2(-1.45, 2.4), mix(vec2(-1.45, 2.4), vec2(0.45, 3.6), grow)) + 0.02);
    tr = min(tr, seg(p, vec2(-1.5, 1.9), mix(vec2(-1.5, 1.9), vec2(-2.4, 3.1), grow)) + 0.03);
    tr = min(tr, seg(p, vec2(-1.45, 2.6), mix(vec2(-1.45, 2.6), vec2(-1.1, 3.7), grow)) + 0.03);
    float wTr = 0.09 - 0.02 * smoothstep(0.0, 3.6, y);
    col = mix(col, vec3(0.06, 0.05, 0.06), (1.0 - smoothstep(wTr * 0.6, wTr, tr)) * step(0.01, grow));
    float zone = 1.0 - smoothstep(0.4, 1.6, length((p - vec2(-1.2, 3.3)) * vec2(0.55, 1.0)));
    float bl = snoise(vec3(p * 2.4, 2.0)) * 0.5 + 0.5;
    float bloom = smoothstep(0.45, 0.52, bl * zone * bloomK + 0.2 * zone * bloomK);
    col = mix(col, mix(vec3(0.86, 0.5, 0.6), vec3(0.98, 0.76, 0.82), snoise(vec3(p * 7.0, 5.0)) * 0.5 + 0.5), bloom);
    // pink petals drifting in the breeze (petal shapes)
    for (int i = 0; i < 12; i++){
      float fi = float(i);
      vec2 c = vec2(mod(-1.4 + hash(fi) * 2.0 + (uT - 9.0) * (0.25 + 0.2 * hash(fi + 2.0)), 9.0) - 2.5, 3.4 - mod((uT - 9.0) * 0.18 + hash(fi + 5.0) * 3.0, 3.6) + 0.15 * sin(uT + fi));
      vec2 q = p - c;
      float a = uT * 1.1 + fi;
      q = mat2(cos(a), -sin(a), sin(a), cos(a)) * q;
      float petal = 1.0 - smoothstep(0.85, 1.0, length(vec2(q.x / (0.04 * (1.0 - clamp(q.y / 0.08, -1.0, 1.0) * 0.6)), q.y / 0.08)));
      col = mix(col, vec3(0.97, 0.7, 0.78), petal * smoothstep(9.0, 10.5, uT));
    }
    local = vec2(X, y - uCy);
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz;
    float edge = -2.1 + 0.25 * sin(xz.x * 1.3) + 0.1 * sin(xz.x * 4.0);
    vec3 rock = mix(vec3(0.1, 0.11, 0.12), vec3(0.17, 0.19, 0.17), n) * (0.85 + 0.25 * n2);
    vec3 drop = mix(vec3(0.02, 0.02, 0.05), vec3(0.08, 0.09, 0.14), snoise(vec3(xz * 0.5, uT * 0.05)) * 0.5 + 0.5);
    col = mix(drop, rock, smoothstep(edge - 0.05, edge + 0.05, xz.y));
    col += vec3(0.25, 0.24, 0.2) * smoothstep(-1.0, -3.0, xz.y) * 0.05;
    local = xz;
  } else {
    col = mix(vec3(0.06, 0.07, 0.13), vec3(0.1, 0.11, 0.18), n);
    local = vPos.xz;
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  col = mix(col, paint, smoothstep(0.0, 0.25, settle * 1.3 - drip * 0.6 - n * 0.25));
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
